import { Match } from '../../types';
import {
  GroupStandingRow,
  QualifiedTeam,
  MundialTeam,
  MUNDIAL_GROUPS,
} from '../../types/mundial';
import { lookupAnnexCThirdGroup } from '../../constants/mundialThirdPlaceAnnexC';
import { calculateGroupStandings, isGroupStageComplete } from './mundialStandingsService';

export interface ThirdPlaceCandidate extends GroupStandingRow {
  group: string;
}

/**
 * Clasificación Mundial 2026: 1° y 2° de cada grupo (24) + 8 mejores terceros.
 */
export function computeQualifiedTeams(
  teams: MundialTeam[],
  matches: Match[]
): { qualified: QualifiedTeam[]; thirdPlaceRanking: ThirdPlaceCandidate[] } {
  const qualified: QualifiedTeam[] = [];
  const thirdCandidates: ThirdPlaceCandidate[] = [];

  MUNDIAL_GROUPS.forEach((groupId) => {
    const groupTeams = teams.filter((t) => t.group.toUpperCase() === groupId);
    const groupMatches = matches.filter(
      (m) => m.group?.toUpperCase() === groupId && m.match_type === 'group'
    );

    if (!isGroupStageComplete(groupTeams, groupMatches)) return;

    const standings = calculateGroupStandings(groupTeams, groupMatches);
    const first = standings[0];
    const second = standings[1];
    const third = standings[2];

    if (first) {
      qualified.push({
        teamCode: first.teamCode,
        teamName: first.teamName,
        flagUrl: first.flagUrl,
        group: groupId,
        position: 1,
        slotKey: `1${groupId}`,
      });
    }
    if (second) {
      qualified.push({
        teamCode: second.teamCode,
        teamName: second.teamName,
        flagUrl: second.flagUrl,
        group: groupId,
        position: 2,
        slotKey: `2${groupId}`,
      });
    }
    if (third) {
      thirdCandidates.push({ ...third, group: groupId });
    }
  });

  thirdCandidates.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
    if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
    return a.group.localeCompare(b.group);
  });

  /** Los 8 mejores terceros clasifican; cada uno ocupa el slot 3{grupo} (ej. 3E = 3° del grupo E). */
  thirdCandidates.slice(0, 8).forEach((t) => {
    qualified.push({
      teamCode: t.teamCode,
      teamName: t.teamName,
      flagUrl: t.flagUrl,
      group: t.group,
      position: 3,
      slotKey: `3${t.group}`,
    });
  });

  return { qualified, thirdPlaceRanking: thirdCandidates };
}

export interface ResolveSlotContext {
  /** Al resolver un slot 3-combo (ej. 3EHIJK), el ganador de grupo que enfrenta (ej. 1L). */
  opponentWinnerSlot?: string;
}

export function resolveSlotToTeam(
  slotKey: string,
  qualified: QualifiedTeam[],
  thirdRanking?: ThirdPlaceCandidate[],
  context?: ResolveSlotContext
): QualifiedTeam | undefined {
  const key = slotKey.toUpperCase();
  const exact = qualified.find((q) => q.slotKey.toUpperCase() === key);
  if (exact) return exact;

  /** Cruces tipo 3ABCDF: tercero asignado por Annex C FIFA según los 8 grupos clasificados. */
  const combo = key.match(/^3([A-L]+)$/);
  if (!combo) return undefined;

  const pool = combo[1].split('');
  const qualifyingThirdGroups = qualified
    .filter((q) => q.position === 3)
    .map((q) => q.group.toUpperCase());

  if (context?.opponentWinnerSlot && qualifyingThirdGroups.length === 8) {
    const winnerGroup = context.opponentWinnerSlot.replace(/^1/, '').toUpperCase();
    const assignedGroup = lookupAnnexCThirdGroup(qualifyingThirdGroups, winnerGroup);
    if (assignedGroup && pool.includes(assignedGroup)) {
      return qualified.find(
        (q) => q.position === 3 && q.group.toUpperCase() === assignedGroup
      );
    }
    return undefined;
  }

  /** Fase de grupos incompleta: solo si hay un candidato en el pool. */
  const candidates = qualified.filter(
    (q) => q.position === 3 && pool.includes(q.group)
  );
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];

  if (thirdRanking) {
    const ranked = thirdRanking
      .filter((t) => candidates.some((c) => c.teamCode === t.teamCode))
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
        return b.goalsFor - a.goalsFor;
      });
    const best = ranked[0];
    return candidates.find((c) => c.teamCode === best?.teamCode);
  }

  return candidates.sort((a, b) => a.group.localeCompare(b.group))[0];
}

export function allGroupStagesComplete(
  teams: MundialTeam[],
  matches: Match[]
): boolean {
  return MUNDIAL_GROUPS.every((groupId) => {
    const groupTeams = teams.filter((t) => t.group.toUpperCase() === groupId);
    const groupMatches = matches.filter(
      (m) => m.group?.toUpperCase() === groupId && m.match_type === 'group'
    );
    return groupTeams.length >= 4 && isGroupStageComplete(groupTeams, groupMatches);
  });
}
